import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAppSettings } from '../../hooks/useAppSettings';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiSave, FiUpload, FiEye, FiRefreshCw } = FiIcons;

const AppCustomization = () => {
  const { appSettings, updateAppSettings, loading } = useAppSettings();
  const [previewMode, setPreviewMode] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      appName: appSettings?.appName || 'MedTracker',
      primaryColor: appSettings?.primaryColor || '#3b82f6',
      secondaryColor: appSettings?.secondaryColor || '#8b5cf6',
      logoUrl: appSettings?.logoUrl || '',
      tagline: appSettings?.tagline || 'Medicine Management System',
      supportEmail: appSettings?.supportEmail || 'support@medtracker.com',
      privacyPolicy: appSettings?.privacyPolicy || '',
      termsOfService: appSettings?.termsOfService || ''
    }
  });

  const watchedValues = watch();

  React.useEffect(() => {
    if (appSettings) {
      Object.keys(appSettings).forEach(key => {
        setValue(key, appSettings[key]);
      });
    }
  }, [appSettings, setValue]);

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        toast.error('Logo file must be less than 2MB');
        return;
      }
      
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setValue('logoUrl', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const onSubmit = async (data) => {
    try {
      await updateAppSettings(data);
      toast.success('App settings updated successfully!');
    } catch (error) {
      toast.error('Failed to update app settings');
      console.error('Error updating settings:', error);
    }
  };

  const resetToDefaults = () => {
    setValue('appName', 'MedTracker');
    setValue('primaryColor', '#3b82f6');
    setValue('secondaryColor', '#8b5cf6');
    setValue('logoUrl', '');
    setValue('tagline', 'Medicine Management System');
    setLogoPreview(null);
    setLogoFile(null);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">App Customization</h2>
          <p className="text-gray-600 mt-1">Customize the app appearance and branding</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={() => setPreviewMode(!previewMode)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              previewMode 
                ? 'bg-purple-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <SafeIcon icon={FiEye} />
            <span>{previewMode ? 'Exit Preview' : 'Preview'}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Settings Form */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* App Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                App Name
              </label>
              <input
                type="text"
                {...register('appName', { required: 'App name is required' })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Enter app name"
              />
              {errors.appName && (
                <p className="mt-1 text-sm text-red-600">{errors.appName.message}</p>
              )}
            </div>

            {/* Logo Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                App Logo
              </label>
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <p className="mt-1 text-sm text-gray-500">PNG, JPG up to 2MB</p>
                </div>
                {(logoPreview || watchedValues.logoUrl) && (
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                    <img 
                      src={logoPreview || watchedValues.logoUrl} 
                      alt="Logo preview"
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Colors */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Color
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    {...register('primaryColor')}
                    className="w-12 h-12 border border-gray-300 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    {...register('primaryColor')}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm font-mono"
                    placeholder="#3b82f6"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Secondary Color
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    {...register('secondaryColor')}
                    className="w-12 h-12 border border-gray-300 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    {...register('secondaryColor')}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm font-mono"
                    placeholder="#8b5cf6"
                  />
                </div>
              </div>
            </div>

            {/* Tagline */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tagline
              </label>
              <input
                type="text"
                {...register('tagline')}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Medicine Management System"
              />
            </div>

            {/* Support Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Support Email
              </label>
              <input
                type="email"
                {...register('supportEmail')}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="support@medtracker.com"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={resetToDefaults}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiRefreshCw} />
                <span>Reset to Defaults</span>
              </button>
              
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50"
              >
                <SafeIcon icon={FiSave} />
                <span>{loading ? 'Saving...' : 'Save Changes'}</span>
              </motion.button>
            </div>
          </form>
        </div>

        {/* Live Preview */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Live Preview</h3>
          
          {/* Preview Header */}
          <div 
            className="rounded-lg p-4 mb-6"
            style={{ 
              background: `linear-gradient(135deg, ${watchedValues.primaryColor}, ${watchedValues.secondaryColor})` 
            }}
          >
            <div className="flex items-center space-x-3 text-white">
              {(logoPreview || watchedValues.logoUrl) ? (
                <img 
                  src={logoPreview || watchedValues.logoUrl} 
                  alt="App logo"
                  className="w-10 h-10 object-contain bg-white bg-opacity-20 rounded-lg p-1"
                />
              ) : (
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiIcons.FiActivity} className="text-xl" />
                </div>
              )}
              <div>
                <h1 className="text-xl font-bold">{watchedValues.appName}</h1>
                <p className="text-sm opacity-90">{watchedValues.tagline}</p>
              </div>
            </div>
          </div>

          {/* Preview Buttons */}
          <div className="space-y-3">
            <button 
              className="w-full py-3 px-4 rounded-lg font-medium text-white transition-colors"
              style={{ backgroundColor: watchedValues.primaryColor }}
            >
              Primary Button Example
            </button>
            <button 
              className="w-full py-3 px-4 rounded-lg font-medium text-white transition-colors"
              style={{ backgroundColor: watchedValues.secondaryColor }}
            >
              Secondary Button Example
            </button>
          </div>

          {/* Preview Card */}
          <div className="mt-6 p-4 border-2 rounded-lg" style={{ borderColor: watchedValues.primaryColor + '20' }}>
            <div className="flex items-center space-x-2 mb-2">
              <div 
                className="w-4 h-4 rounded-full"
                style={{ backgroundColor: watchedValues.primaryColor }}
              ></div>
              <span className="font-medium text-gray-900">Sample Medicine Card</span>
            </div>
            <p className="text-sm text-gray-600">This is how cards will look with your color scheme</p>
          </div>

          {/* Color Palette */}
          <div className="mt-6">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Color Palette</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center">
                <div 
                  className="w-full h-12 rounded-lg mb-2"
                  style={{ backgroundColor: watchedValues.primaryColor }}
                ></div>
                <p className="text-xs text-gray-600">Primary</p>
                <p className="text-xs font-mono text-gray-500">{watchedValues.primaryColor}</p>
              </div>
              <div className="text-center">
                <div 
                  className="w-full h-12 rounded-lg mb-2"
                  style={{ backgroundColor: watchedValues.secondaryColor }}
                ></div>
                <p className="text-xs text-gray-600">Secondary</p>
                <p className="text-xs font-mono text-gray-500">{watchedValues.secondaryColor}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppCustomization;